/* eslint-disable */
import { useRouter } from "next/router";
import { useRecoilCallback, useRecoilValue } from "recoil";

import { GetCompletedEvalByFilterDocument } from "graphql/generated";
import graphQLClient from "graphql/react-query";
import { evaluationLSName } from "src/constants/local-storage-names.constant";
import {
  currentEvaluationIdState,
  currentJumpToTreeState,
  currentSectionIndexState,
  currentSectionState,
  evaluationStepperState,
  formSectionsState,
  patientState,
  sectionVisitsState,
  totalSectionsState
} from "src/states";

export const useEvaluationNav = () => {
  const router = useRouter();

  const patient = useRecoilValue(patientState);
  const evalId = useRecoilValue(currentEvaluationIdState);

  const back = useRecoilCallback(({ snapshot }) => async () => {
    const step = await snapshot.getPromise(evaluationStepperState);

    if (step === "intro") await router.push("/");
    if (step === "evaluation") await backFromEvaluation();
    if (step === "goals") await backFromGoal();
  });

  const backFromGoal = useRecoilCallback(({ set }) => () => {
    set(evaluationStepperState, "intro");
  });

  const backFromEvaluation = useRecoilCallback(({ set, snapshot }) => async () => {
    const sectionIndex = await snapshot.getPromise(currentSectionIndexState);
    const visits = await snapshot.getPromise(sectionVisitsState);
    // const formQandA = await snapshot.getPromise(formQandAState);
    // const currentJumpToTree = await snapshot.getPromise(currentJumpToTreeState);

    if (sectionIndex === 0) {
      set(evaluationStepperState, "intro");
      return;
    }

    if (visits.length > 0) {
      const arr = [...visits];
      const previousIndex = arr.pop();

      set(sectionVisitsState, arr);
      set(currentSectionIndexState, previousIndex);
    }
  });

  const next = useRecoilCallback(({ set, snapshot }) => async (values) => {
    const sectionIndex = await snapshot.getPromise(currentSectionIndexState);
    const nextIndex = await onSectionCompleted(values);

    set(sectionVisitsState, (d) => [...d, sectionIndex]);
    set(currentSectionIndexState, nextIndex);
  });

  const findSectionIndexByQuestionId = (formSections, id: string) => {
    let index = 0;

    for (; index < formSections.length; index++) {
      const formSection = formSections[index];
      if (formSection.questions) {
        for (const question of formSection.questions) {
          if (question.questionId === id) {
            return index;
          }
        }
      }
    }

    return null;
  };

  const findSectionIndexbySectionId = (formSections, id: string) => {
    let index = 0;

    for (; index < formSections.length; index++) {
      const formSection = formSections[index];
      if (formSection?.sectionId != null && formSection.sectionId === id) {
        return index;
      }
    }

    return null;
  };

  const findNextIndexes = (formSections, question, values, indexes) => {
    let nextIndexes = [...indexes];

    const anw = values[question.questionId];

    if (question?.options?.anwsers) {
      for (const anwser of question.options.anwsers) {
        if (anwser.value === anw || anw.includes(anwser.value)) {
          let sectionIndexGoTo = null;
          if (anwser.goToQuestion) {
            sectionIndexGoTo = findSectionIndexByQuestionId(formSections, anwser.goToQuestion);
          } else if (anwser.goToSection) {
            sectionIndexGoTo = findSectionIndexbySectionId(formSections, anwser.goToSection);
          }

          if (sectionIndexGoTo != null) {
            if (!nextIndexes.includes(sectionIndexGoTo)) {
              nextIndexes.push(sectionIndexGoTo);
            }
          }
        }
      }
    }

    if (question.defaultGoToQuestion && nextIndexes.length <= 0) {
      nextIndexes = [findSectionIndexByQuestionId(formSections, question.defaultGoToQuestion)];
    }

    nextIndexes.sort((a, b) => a - b);

    return nextIndexes;
  };

  const pushingNewQandA = (currentQA, question, values) => {
    const clonedQA: any[] = [...currentQA];

    const skipped = !values.hasOwnProperty(question.questionId);

    const QandA = {
      q: question.question,
      a: skipped ? null : values[question.questionId],
      questionId: question.questionId,
      skipped,
      runSpecialFunctionType:
        question?.runSpecialFunction?.type ||
        (typeof question?.runSpecialFunction === "string" && question?.runSpecialFunction) ||
        ""
    };

    const existIndex: number = clonedQA.findIndex(
      (item: any) => item.questionId === question.questionId
    );

    if (existIndex > -1) {
      clonedQA[existIndex] = QandA;
    } else {
      clonedQA.push(QandA);
    }

    return clonedQA;
  };

  const findNextIndex = (clonedJumpToTree, nextIndexes, sectionIndex) => {
    let nextIndex = null;

    if (clonedJumpToTree.length > 1) {
      const currentSectionIndex = clonedJumpToTree.indexOf(sectionIndex);

      if (currentSectionIndex > -1) {
        if (currentSectionIndex === clonedJumpToTree.length - 1) {
          clonedJumpToTree = [];
        } else {
          nextIndex = clonedJumpToTree[currentSectionIndex + 1];
        }
      } else {
        clonedJumpToTree = [];
      }
    }

    if (clonedJumpToTree.length <= 0) {
      if (nextIndexes.length > 1) {
        clonedJumpToTree = nextIndexes;
      }
      if (nextIndexes.length > 0) {
        nextIndex = nextIndexes[0];
      }
    }

    nextIndex = !nextIndex ? sectionIndex + 1 : nextIndex;

    return [nextIndex, clonedJumpToTree];
  };

  const onSectionCompleted = useRecoilCallback(({ set, snapshot }) => async (values) => {
    // TODO: should be split into hook to reuse
    const evalData = patient
      ? await graphQLClient.request(GetCompletedEvalByFilterDocument, {
          input: {
            patientId: patient?.sysId,
            evaluationId: evalId
          }
        })
      : null;

    const localEval = localStorage.getItem(evaluationLSName(evalId));
    const localEvalData = JSON.parse(localEval || null);
    const formQandA =
      evalData?.getCompletedEvalByFilter?.[0]?.formQandA || localEvalData?.formQandA || [];
    const currentJumpToTree = await snapshot.getPromise(currentJumpToTreeState);
    const currentSection = await snapshot.getPromise(currentSectionState);
    const formSections = await snapshot.getPromise(formSectionsState);
    const sectionIndex = await snapshot.getPromise(currentSectionIndexState);
    const totalSections = await snapshot.getPromise(totalSectionsState);

    let nextIndexes = [];
    let clonedQA: any[] = [...formQandA];
    const clonedJumpToTree = [...currentJumpToTree];

    if (values) {
      for (const question of currentSection.questions) {
        const skipped = !values.hasOwnProperty(question.questionId);
        clonedQA = pushingNewQandA(clonedQA, question, values);
        if (!skipped) nextIndexes = findNextIndexes(formSections, question, values, nextIndexes);
      }
    }

    const [nextIndex, newJumpToTree] = findNextIndex(clonedJumpToTree, nextIndexes, sectionIndex);

    // await save(clonedQA, nextIndex + 1 > totalSections, sectionIndex, nextIndex, newJumpToTree);

    set(currentJumpToTreeState, newJumpToTree);

    return nextIndex;
  });

  return { back, next };
};
